
import React, { useState, useMemo } from 'react';
import { View, UserRole } from '../types.ts';
import { viewColors } from '../constants.ts';
import { APP_THEMES, getThemeById } from '../src/themeConfig.ts';
import { 
  LayoutDashboard, 
  Users, 
  NotebookPen, 
  CalendarCheck, 
  BookMarked,
  CalendarDays,
  Target,
  User as UserIcon,
  Sparkles,
  BarChart3,
  Database as DatabaseIcon,
  ClipboardList,
  History,
  Building2,
  MoreHorizontal,
  X,
  Award,
  Layers,
  GraduationCap,
  UserCheck
} from 'lucide-react';

interface BottomNavProps {
  role: UserRole;
  activeView: View;
  setActiveView: (view: View) => void;
  isMaintenance?: boolean;
  appTheme?: string;
}

const BottomNav: React.FC<BottomNavProps> = ({ role, activeView, setActiveView, isMaintenance, appTheme = 'wzc-themes' }) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);

  const defaultActiveGradient = currentTheme.activeNavGradient || 'from-indigo-600 via-indigo-500 to-violet-600';

  const adminItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, label: 'Beranda', activeGradient: defaultActiveGradient, activeText: 'text-indigo-600 dark:text-indigo-400', glow: 'shadow-indigo-500/30' },
    { view: View.ManageStudents, icon: <Users size={20} />, label: 'Siswa', activeGradient: 'from-blue-600 via-blue-500 to-cyan-600', activeText: 'text-blue-600 dark:text-blue-400', glow: 'shadow-blue-500/30' },
    { view: View.ManageTeachers, icon: <GraduationCap size={20} />, label: 'Guru', activeGradient: 'from-emerald-600 via-emerald-500 to-teal-600', activeText: 'text-emerald-600 dark:text-emerald-400', glow: 'shadow-emerald-500/30' },
    { view: View.ManageClasses, icon: <Building2 size={20} />, label: 'Kelas', activeGradient: 'from-amber-600 via-amber-500 to-orange-600', activeText: 'text-amber-600 dark:text-amber-400', glow: 'shadow-amber-500/30' },
  ];

  const allAdminItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, label: 'Beranda', tileBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border-indigo-200/50 dark:border-indigo-900/40' },
    { view: View.ManageStudents, icon: <Users size={20} />, label: 'Siswa', tileBg: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/50 dark:border-blue-900/40' },
    { view: View.ManageTeachers, icon: <GraduationCap size={20} />, label: 'Guru', tileBg: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-900/40' },
    { view: View.ManageClasses, icon: <Building2 size={20} />, label: 'Kelas', tileBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200/50 dark:border-amber-900/40' },
    { view: View.LearningObjectives, icon: <Target size={20} />, label: 'Tujuan Pembelajaran', tileBg: 'bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-400 border-pink-200/50 dark:border-pink-900/40' },
    { view: View.AdminAgenda, icon: <BookMarked size={20} />, label: 'Agenda Guru', tileBg: 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 border-orange-200/50 dark:border-orange-900/40' },
    { view: View.Schedule, icon: <CalendarDays size={20} />, label: 'Kalender', tileBg: 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 border-sky-200/50 dark:border-sky-900/40' },
    { view: View.Settings, icon: <Building2 size={20} />, label: 'Pengaturan', tileBg: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
    { view: View.Database, icon: <DatabaseIcon size={20} />, label: 'Database', tileBg: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200/50 dark:border-rose-900/40' },
  ];

  const guruItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, label: 'Beranda', activeGradient: defaultActiveGradient, activeText: 'text-indigo-600 dark:text-indigo-400', glow: 'shadow-indigo-500/30' },
    { view: View.Schedule, icon: <CalendarDays size={20} />, label: 'Jadwal', activeGradient: 'from-sky-600 via-sky-500 to-blue-600', activeText: 'text-sky-600 dark:text-sky-400', glow: 'shadow-sky-500/30' },
    { view: View.Agenda, icon: <BookMarked size={20} />, label: 'Agenda', activeGradient: 'from-emerald-600 via-emerald-500 to-teal-600', activeText: 'text-emerald-600 dark:text-emerald-400', glow: 'shadow-emerald-500/30' },
    { view: View.Attendance, icon: <CalendarCheck size={20} />, label: 'Presensi', activeGradient: 'from-rose-600 via-rose-500 to-pink-600', activeText: 'text-rose-600 dark:text-rose-400', glow: 'shadow-rose-500/30' },
  ];

  const siswaItems = [
    { view: View.StudentProfile, icon: <UserIcon size={20} />, label: 'Profil', activeGradient: defaultActiveGradient, activeText: 'text-indigo-600 dark:text-indigo-400', glow: 'shadow-indigo-500/30' },
    { view: View.StudentGrade, icon: <Award size={20} />, label: 'Nilai Saya', activeGradient: 'from-amber-600 via-amber-500 to-orange-600', activeText: 'text-amber-600 dark:text-amber-400', glow: 'shadow-amber-500/30' },
    { view: View.Tugas, icon: <ClipboardList size={20} />, label: 'Tugas', activeGradient: 'from-fuchsia-600 via-fuchsia-500 to-pink-600', activeText: 'text-fuchsia-600 dark:text-fuchsia-400', glow: 'shadow-fuchsia-500/30' },
    { view: View.SubjectSelection, icon: <BookMarked size={20} />, label: 'Pilih Mapel', activeGradient: 'from-blue-600 via-blue-500 to-indigo-600', activeText: 'text-blue-600 dark:text-blue-400', glow: 'shadow-blue-500/30' },
  ];

  const allGuruItems = [
    { view: View.Dashboard, icon: <LayoutDashboard size={20} />, label: 'Beranda', tileBg: 'bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border-indigo-200/50 dark:border-indigo-900/40' },
    { view: View.Schedule, icon: <CalendarDays size={20} />, label: 'Jadwal', tileBg: 'bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 border-sky-200/50 dark:border-sky-900/40' },
    { view: View.LearningObjectives, icon: <Target size={20} />, label: 'TP', tileBg: 'bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-400 border-pink-200/50 dark:border-pink-900/40' },
    { view: View.Perwalian, icon: <UserCheck size={20} />, label: 'Administrasi Walas', tileBg: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-900/40' },
    { view: View.Agenda, icon: <BookMarked size={20} />, label: 'Agenda', tileBg: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200/50 dark:border-emerald-900/40' },
    { view: View.Attendance, icon: <CalendarCheck size={20} />, label: 'Presensi', tileBg: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border-rose-200/50 dark:border-rose-900/40' },
    { view: View.Grading, icon: <NotebookPen size={20} />, label: 'Input Nilai', tileBg: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border-amber-200/50 dark:border-amber-900/40' },
    { view: View.NilaiRapor, icon: <BarChart3 size={20} />, label: 'Nilai Rapor', tileBg: 'bg-teal-50 dark:bg-teal-950/40 text-teal-600 dark:text-teal-400 border-teal-200/50 dark:border-teal-900/40' },
    { view: View.MaterialGenerator, icon: <Sparkles size={20} />, label: 'Asisten AI', tileBg: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border-purple-200/50 dark:border-purple-900/40' },
    { view: View.Tugas, icon: <ClipboardList size={20} />, label: 'Tugas', tileBg: 'bg-fuchsia-50 dark:bg-fuchsia-950/40 text-fuchsia-600 dark:text-fuchsia-400 border-fuchsia-200/50 dark:border-fuchsia-900/40' },
    { view: View.StudentList, icon: <Users size={20} />, label: 'Data Siswa', tileBg: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border-blue-200/50 dark:border-blue-900/40' },
    { view: View.Settings, icon: <Building2 size={20} />, label: 'Pengaturan', tileBg: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700' },
    { view: View.ProfileAccessHistory, icon: <History size={20} />, label: 'Riwayat', tileBg: 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-600 dark:text-cyan-400 border-cyan-200/50 dark:border-cyan-900/40' },
  ];

  const navItems = role === UserRole.Admin ? adminItems : role === UserRole.Guru ? guruItems : siswaItems;
  const moreItems = role === UserRole.Admin 
    ? allAdminItems.filter(item => !adminItems.some(a => a.view === item.view)) 
    : role === UserRole.Guru 
    ? allGuruItems.filter(item => !guruItems.some(g => g.view === item.view)) 
    : [];

  return (
    <>
      {/* Sleek Floating Bottom Navigation Bar for Mobile */}
      <div className="lg:hidden fixed bottom-3 left-3 right-3 z-50 bg-white/85 dark:bg-slate-900/85 backdrop-blur-2xl border border-slate-200/80 dark:border-slate-800/80 rounded-2xl shadow-[0_12px_32px_-6px_rgba(0,0,0,0.15)] dark:shadow-[0_12px_32px_-6px_rgba(0,0,0,0.6)] px-2 py-1.5 transition-all">
        <div className="flex items-center justify-around w-full">
          {navItems.map((item) => {
            const isActive = activeView === item.view;
            const isStudentItem = role === UserRole.Siswa;
            const isDisabled = isStudentItem && isMaintenance;

            return (
              <button
                key={item.view}
                onClick={() => !isDisabled && setActiveView(item.view)}
                disabled={isDisabled}
                className={`flex flex-col items-center justify-center gap-1 px-1.5 py-1 transition-all duration-300 relative rounded-xl ${
                  isActive ? 'scale-110 -translate-y-0.5' : isDisabled ? 'opacity-40 grayscale cursor-not-allowed' : 'hover:opacity-100 opacity-65'
                }`}
              >
                {/* Active Floating Pill with Soft Gradient & Glow */}
                <div className={`p-2 rounded-2xl transition-all duration-300 flex items-center justify-center ${
                  isActive 
                    ? `bg-gradient-to-tr ${item.activeGradient} text-white shadow-lg ${item.glow}` 
                    : isDisabled 
                    ? 'text-slate-400 dark:text-slate-600' 
                    : 'bg-transparent text-slate-600 dark:text-slate-400'
                }`}>
                  {React.cloneElement(item.icon as React.ReactElement, { 
                    size: isActive ? 20 : 19
                  } as any)}
                </div>

                <span className={`text-[9px] font-black tracking-tight truncate max-w-[60px] text-center transition-colors ${
                  isActive 
                    ? item.activeText 
                    : isDisabled 
                    ? 'text-slate-400 dark:text-slate-600' 
                    : 'text-slate-500 dark:text-slate-400 font-bold'
                }`}>
                  {item.label || item.view}
                </span>

                {/* Subtle active dot */}
                {isActive && (
                  <span className={`absolute -bottom-1 w-1 h-1 rounded-full animate-pulse bg-gradient-to-r ${item.activeGradient}`} />
                )}
              </button>
            );
          })}
          
          {moreItems.length > 0 && (
            <button
              onClick={() => setIsMoreOpen(true)}
              className={`flex flex-col items-center justify-center gap-1 px-1.5 py-1 transition-all duration-300 relative rounded-xl ${
                isMoreOpen ? 'scale-110 -translate-y-0.5' : 'hover:opacity-100 opacity-65'
              }`}
            >
              <div className={`p-2 rounded-2xl transition-all duration-300 flex items-center justify-center ${
                isMoreOpen 
                  ? 'bg-gradient-to-tr from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 text-white shadow-lg shadow-slate-900/20' 
                  : 'bg-transparent text-slate-600 dark:text-slate-400'
              }`}>
                <MoreHorizontal size={19} />
              </div>
              <span className={`text-[9px] font-black tracking-tight transition-colors ${
                isMoreOpen ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400 font-bold'
              }`}>
                Lainnya
              </span>
            </button>
          )}
        </div>
      </div>

      {/* More Items Modal Sheet */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-[60] bg-slate-950/50 backdrop-blur-md flex items-end justify-center p-3 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden animate-in slide-in-from-bottom duration-300">
            <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/80 dark:bg-slate-800/50">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/30">
                  <Layers size={18} />
                </div>
                <div>
                  <h2 className="text-sm font-black text-slate-900 dark:text-white leading-tight">
                    Menu Administrasi
                  </h2>
                  <p className="text-[10px] font-medium text-slate-400">
                    Menu & Fitur Tambahan {role === UserRole.Guru ? 'Guru' : 'Admin'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsMoreOpen(false)} 
                className="p-2 bg-slate-200/70 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full hover:bg-rose-100 hover:text-rose-600 dark:hover:bg-rose-950/50 dark:hover:text-rose-400 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 grid grid-cols-3 gap-3 max-h-[65vh] overflow-y-auto custom-scrollbar">
              {moreItems.map((item) => {
                return (
                  <button
                    key={item.view}
                    onClick={() => {
                      setActiveView(item.view);
                      setIsMoreOpen(false);
                    }}
                    className="flex flex-col items-center justify-center gap-2 p-3 bg-slate-50/60 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 border border-slate-200/60 dark:border-slate-800 hover:border-indigo-200 dark:hover:border-indigo-800/60 hover:shadow-md rounded-2xl transition-all group active:scale-95"
                  >
                    <div className={`p-2.5 rounded-2xl border transition-all group-hover:scale-110 shadow-sm ${item.tileBg}`}>
                      {React.cloneElement(item.icon as React.ReactElement, { size: 20 } as any)}
                    </div>
                    <span className="text-[10px] font-black text-slate-700 dark:text-slate-300 text-center leading-tight">
                      {item.label || item.view}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;

